import { AD_UNIT_TYPE } from '../../../common/js/adUnit';
import { hasGoodEffectiveConnection } from '../../../common/js/connection';
import { excludedMimeType } from '../config';
import { MediaFile } from './vast/media-file';

interface Context {
  advertDeliveryType?: string;
  adUnitType?: string;
}

const DELIVERY_TYPE: Record<string, { MAX: number }> = {
  PRECACHING: {
    MAX: 4 // mB
  },
  ADCHOOSER: {
    MAX: 3 // mB
  }
};

const KBITS_TO_MBYTES_DENOMINATOR = 1000 * 8;

export function getVideoSize(mediaFile: MediaFile): number {
  const { bitrate = 0, duration = 0 } = mediaFile || {};
  return (bitrate * duration) / KBITS_TO_MBYTES_DENOMINATOR;
}

/**
 * @param videoSize
 * @param advertDeliveryType
 * @returns {boolean}
 */
export const isVideoSizeAcceptable = (
  videoSize: number,
  advertDeliveryType?: string
): boolean => {
  let deliverytypeElement =
    DELIVERY_TYPE[(advertDeliveryType || '').toUpperCase()];

  if (!deliverytypeElement) {
    // unrecognized delivery type, so set default
    deliverytypeElement = DELIVERY_TYPE.PRECACHING;
  }
  return videoSize <= deliverytypeElement.MAX;
};

export const adUnitNeedsHighResolution = (adUnitType: string): boolean => {
  return (
    [
      AD_UNIT_TYPE.OPTIN_VIDEO,
      AD_UNIT_TYPE.INTERSTITIAL,
      AD_UNIT_TYPE.THUMBNAIL,
      AD_UNIT_TYPE.HEADER_AD,
      AD_UNIT_TYPE.FOOTER_AD
    ].indexOf(adUnitType as any) !== -1
  );
};

export const isMediaFilePortrait = (mediaFile: MediaFile): boolean => {
  return mediaFile.height < mediaFile.width;
};

function mediaFileIsExcluded(type: string | undefined): boolean {
  return excludedMimeType.indexOf((type || '').toLowerCase()) >= 0;
}

const MEDIA_FILE_PROPS = ['width', 'height', 'bitrate', 'duration'] as const;

function hasAllProps(mediaFile: MediaFile): boolean {
  return MEDIA_FILE_PROPS.every(
    prop => mediaFile.hasOwnProperty(prop) && (mediaFile as any)[prop] > 0
  );
}

interface MediaFileWithVideoSize extends MediaFile {
  videoSize?: number;
}

/**
 * Function that filters mediaFiles by size / type
 * Mutates the original mediaFile objects to include videoSize property
 *
 * @param mediaFiles
 * @param advertDeliveryType
 * @returns {*[]|Array}
 */
function getBestSizeCandidates(
  mediaFiles: MediaFileWithVideoSize[],
  advertDeliveryType?: string
): MediaFileWithVideoSize[] {
  const allowedMediaFiles: MediaFileWithVideoSize[] = [];
  let fallthroughMediaFile: MediaFileWithVideoSize | undefined;
  const mediaFilesWithIssues: MediaFileWithVideoSize[] = [];

  mediaFiles.forEach(mediaFile => {
    if (!mediaFile.type || mediaFileIsExcluded(mediaFile.type)) {
      return;
    }
    const allProps = hasAllProps(mediaFile);
    if (!allProps) {
      mediaFile.videoSize = 0;
      // the mediaFile was missing a prop or the prop value is not allowed
      mediaFilesWithIssues.push(mediaFile);
      return;
    }
    const videoSize = getVideoSize(mediaFile);
    mediaFile.videoSize = videoSize;

    // memorize the smallest media file
    // if in case later there are no 'best candidates'
    if (
      !fallthroughMediaFile ||
      videoSize < (fallthroughMediaFile.videoSize || 0)
    ) {
      fallthroughMediaFile = mediaFile;
    }

    if (isVideoSizeAcceptable(videoSize, advertDeliveryType)) {
      allowedMediaFiles.push(mediaFile);
    }
  });

  // if there are no best candidates
  // select the smallest video size that was memorized
  if (allowedMediaFiles.length === 0 && fallthroughMediaFile) {
    return [fallthroughMediaFile];
  }

  // if the best candidates are still empty even after attempting to
  // include the smallest video file
  // we add the mediaFiles that had missing props/ invalid props
  // as the best candidate
  if (allowedMediaFiles.length === 0 && mediaFilesWithIssues.length > 0) {
    // TODO: send track that video without width | height | bitrate | duration was selected
    // since nothing else was available
    return mediaFilesWithIssues;
  }

  return allowedMediaFiles;
}

interface DeviceDimensions {
  deviceHeight: number;
  deviceWidth: number;
  devicePortrait: boolean;
}

function getDeviceDimensions(): DeviceDimensions {
  return {
    deviceHeight: window.screen.height,
    deviceWidth: window.screen.width,
    devicePortrait: window.screen.height > window.screen.width
  };
}

function getDeviceConfig(mediaFile: MediaFile): ['height' | 'width', number] {
  const { deviceHeight, deviceWidth, devicePortrait } = getDeviceDimensions();
  const videoPortrait = isMediaFilePortrait(mediaFile);
  switch (true) {
    case videoPortrait && devicePortrait:
      // video is in portrait and device is in portrait
      return ['height', deviceHeight];
    case videoPortrait && !devicePortrait:
      // video is in portrait and device is in landscape
      return ['height', deviceWidth];
    default:
      // when the video is in landscape
      // it does not matter which dimension
      // we return
      return ['height', deviceHeight];
  }
}

function calculatePercentDifference(
  nominator: number,
  denominator?: number
): number {
  return Math.abs(1 - nominator / (denominator || 1));
}

/**
 * Method that gets unique mediaFiles if more than one mediafile shares the same height/width
 * The media file with the highest bit rate is selected
 *
 * @param mediaFiles
 * @param key
 * @returns {Array<MediaFile>} sorted DESC by height/width
 */
function getUniqueMediaFilesByMaxBitrate(
  mediaFiles: MediaFileWithVideoSize[],
  key: 'height' | 'width'
): Array<MediaFileWithVideoSize> {
  const res: Map<number, MediaFileWithVideoSize> = new Map();
  mediaFiles.forEach(mediaFile => {
    const value = mediaFile[key];
    const existing = res.get(value);
    if (!res.has(value) || (existing && existing.bitrate < mediaFile.bitrate)) {
      res.set(value, mediaFile);
    }
  });
  return [...res.values()].sort((a, b) => b[key] - a[key]);
}

/**
 * Method that chooses the first media file that has a lower
 * video prop (width/height) than the device screen (width/height)
 *
 * @param mediaFiles
 * @param videoDimensionKey height|width string to access the mediaFile
 * @param deviceValue height|width number of the screen
 */
function getBestResolutionCandidate(
  mediaFiles: Array<MediaFileWithVideoSize>,
  videoDimensionKey: 'height' | 'width',
  deviceValue?: number
): MediaFileWithVideoSize | undefined {
  const uniqueMediaFiles = getUniqueMediaFilesByMaxBitrate(
    mediaFiles,
    videoDimensionKey
  );
  let previousMediaFile: MediaFileWithVideoSize | undefined;
  let prevDifference = Infinity; // 0 --> 1

  for (let i = 0; i < uniqueMediaFiles.length; i++) {
    const mediaFile = uniqueMediaFiles[i];
    // 0 --> 1
    const diff = calculatePercentDifference(
      mediaFile[videoDimensionKey],
      deviceValue
    );

    if (mediaFile[videoDimensionKey] <= (deviceValue || 0)) {
      if (previousMediaFile && diff > prevDifference) {
        return previousMediaFile;
      }

      // default return current media file
      return mediaFile;
    }

    previousMediaFile = mediaFile;
    prevDifference = diff;
  }

  // most likely every media file
  // was larger than the device
  // send last one
  return uniqueMediaFiles.pop();
}

export const selectBestMediaFiles = (
  mediaFiles: MediaFileWithVideoSize[],
  context: Context = {}
): MediaFileWithVideoSize[] => {
  const allowedMediaFiles = getBestSizeCandidates(
    mediaFiles,
    context.advertDeliveryType
  );
  if (!allowedMediaFiles.length) {
    return [];
  }

  // Sort by lowest resolution
  // TODO: take into accoutn videoSize because multiple meida files
  // with the same width/height can exist in the video size wrong order
  allowedMediaFiles.sort((a, b) => a.height - b.height);
  let shouldUseHighResolution =
    adUnitNeedsHighResolution(context.adUnitType || '') &&
    hasGoodEffectiveConnection();

  if (!shouldUseHighResolution) {
    // return lowest resolution mediaFile
    // TODO: same height/width but not ordered by video size
    return allowedMediaFiles;
  }

  const [
    selectedVideoDimensionKey,
    selectedDeviceDimensionValue
  ] = getDeviceConfig(allowedMediaFiles[0]);

  const bestCandidate = getBestResolutionCandidate(
    allowedMediaFiles,
    selectedVideoDimensionKey,
    selectedDeviceDimensionValue
  );

  return bestCandidate ? [bestCandidate] : [];
};

export const __TESTS__ = {
  getBestResolutionCandidate,
  getVideoSize,
  getBestSizeCandidates,
  hasAllProps,
  getUniqueMediaFilesByMaxBitrate
};
